import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Card, CardActionArea, CardMedia, CardContent, Paper, Button, TextField } from "@mui/material";
import { fetchRecipes } from "../utils/helpers/recipeHelper";
import type { IRecipeWithId } from "../types/recipe";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";

interface IRecipePickerProps {
    handlePostRecipes: (savedRecipes: IRecipeWithId[], selectedRecipes: Set<string>) => void;
    postRecipes: IRecipeWithId[];
}

const RecipePicker: React.FC<IRecipePickerProps> = ({ handlePostRecipes, postRecipes }) => {
    const [loading, setLoading] = useState(true);
    const [selectedRecipes, setSelectedRecipes] = useState<Set<string>>(new Set());
    const [savedRecipes, setSavedRecipes] = useState<IRecipeWithId[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    useEffect(() => {
        fetchRecipes().then((res) => {
            setSavedRecipes(res.data.body);
            setSelectedRecipes(
                new Set(
                    postRecipes.map((recipe) => {
                        return recipe._id;
                    })
                )
            );

            setLoading(false);
        });
    }, [postRecipes]);

    const toggleSelection = useCallback((id: string) => {
        setSelectedRecipes((prev) => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    }, []);

    const filteredRecipes = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return savedRecipes;
        return savedRecipes.filter(
            (recipe) => recipe.name.toLowerCase().includes(query) || recipe.description?.toLowerCase().includes(query)
        );
    }, [savedRecipes, searchQuery]);

    const renderRecipes = useMemo(
        () =>
            filteredRecipes.map((recipe: IRecipeWithId) => {
                const isSelected = selectedRecipes.has(recipe._id);
                return (
                    <Box
                        key={recipe._id}
                        sx={{
                            flex: "1 0 240px",
                            maxWidth: "300px",
                            transition: "flex-basis 0.3s ease",
                        }}
                    >
                        <Card
                            elevation={isSelected ? 6 : 2}
                            sx={{
                                height: 300,
                                display: "flex",
                                flexDirection: "column",
                                border: isSelected ? "2px solid " + theme.palette.primary.main : "1px solid #e0e0e0",
                                cursor: "pointer",
                                overflow: "hidden",
                                transition: "all 0.3s ease",
                            }}
                        >
                            <CardActionArea sx={{ height: "100%" }} onClick={() => toggleSelection(recipe._id)}>
                                <CardMedia
                                    component="img"
                                    height="140"
                                    image={recipe.image ? recipe.image.url : "/images/recipePlaceholder.png"}
                                    alt={recipe.name}
                                    sx={{ objectFit: "cover" }}
                                />
                                <CardContent sx={{ flexGrow: 1, overflow: "hidden", position: "relative" }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 0.5,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            transition: "all 0.2s ease",
                                        }}
                                        title={recipe.name}
                                    >
                                        {recipe.name}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            transition: "all 0.2s ease",
                                        }}
                                    >
                                        {recipe.description}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Box>
                );
            }),
        [filteredRecipes, selectedRecipes, theme.palette.primary.main, toggleSelection]
    );

    if (loading) return <Box />;

    return (
        <Box
            sx={{
                px: { xs: 0, sm: 4 },
                pt: { xs: 0, sm: 4 },
                height: "90dvh",
                maxWidth: 1200,
                mx: "auto",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <TextField
                    label="Search Recipes"
                    variant="filled"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ mb: 3 }}
                />

                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "stretch",
                        justifyContent: "center",
                    }}
                >
                    {renderRecipes}
                </Box>
                <Paper
                    elevation={3}
                    sx={{
                        gap: 1,
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "background.paper",
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handlePostRecipes(savedRecipes, selectedRecipes)}
                        disabled={selectedRecipes.size === 0}
                        sx={{ color: "white" }}
                    >
                        Post Recipes ({selectedRecipes.size})
                    </Button>
                </Paper>
            </Box>
        </Box>
    );
};

export default RecipePicker;
